import type { AnyRouter } from '@tanstack/react-router';
import { getAppBundleFromUrl } from '@/utils/navigation';
import { accountStore } from '@/services/auth/store';
import { recordRumEmbeddedTransition } from '@/services/rum';

// This bridge intentionally touches TanStack Router internals. Keep that
// private API contract documented in this directory's README.
function canSyncRouterLocationWithoutLoad(router: AnyRouter) {
  return (
    typeof router.parseLocation === 'function' &&
    typeof router.batch === 'function' &&
    router.stores?.location?.set != null &&
    router.stores?.resolvedLocation?.set != null
  );
}

function getLocationPath(location: { pathname: string; searchStr?: string; search?: string; hash: string }) {
  const search = location.searchStr ?? location.search ?? '';
  const hash = location.hash === '' || location.hash.startsWith('#') ? location.hash : `#${location.hash}`;

  return `${location.pathname}${search}${hash}`;
}

export function shouldSyncEmbeddedAppLocationWithoutLoad(currentPath: string, nextPath: string) {
  if (currentPath === nextPath) {
    return false;
  }

  const account = accountStore.get();
  const currentAppBundle = getAppBundleFromUrl(currentPath, account);
  const nextAppBundle = getAppBundleFromUrl(nextPath, account);

  return currentAppBundle != null && currentAppBundle === nextAppBundle;
}

export function syncRouterLocationWithoutLoad(router: AnyRouter) {
  if (!canSyncRouterLocationWithoutLoad(router)) {
    return false;
  }

  const previousLocation = router.latestLocation;
  const nextLocation = router.parseLocation(router.history.location, previousLocation);

  router.latestLocation = nextLocation;
  router.batch(() => {
    router.stores.location.set(nextLocation);
    router.stores.resolvedLocation.set(nextLocation);
  });

  return true;
}

export function installEmbeddedAppHistorySync(router: AnyRouter) {
  const load = router.load.bind(router);

  router.load = async (options) => {
    if (!canSyncRouterLocationWithoutLoad(router)) {
      await load(options);
      return;
    }

    const currentPath = getLocationPath(router.latestLocation);
    const nextHistoryLocation = router.parseLocation(router.history.location, router.latestLocation);
    const nextPath = getLocationPath(nextHistoryLocation);

    if (shouldSyncEmbeddedAppLocationWithoutLoad(currentPath, nextPath)) {
      const previousPathname = router.latestLocation.pathname;
      if (syncRouterLocationWithoutLoad(router)) {
        // The only signal an intra-embedded-app page transition produces: the
        // sync path never runs the navigation lifecycle, so RUM's router
        // subscriptions can't see it. Same-pathname syncs (search/hash-only
        // changes) are not page transitions.
        if (nextHistoryLocation.pathname !== previousPathname) {
          recordRumEmbeddedTransition(nextHistoryLocation.pathname);
        }
        return;
      }
    }

    await load(options);
  };
}
